/*
 *
 * Experimental component designed to provide a "standard" component for the main area of a page
 * including all the standard logic/ui such as "spinner" for loading, error message when
 * data fetching fails, etc.
 */

import {
  Page,
  P,
  BottomBar,
  SaveBar,
  Spinner,
  Error,
  Gap,
  Button,
  RightSidebar,
} from '@salecycle/ui-component-library';
import AppTopBar from 'core/ui/components/AppTopBar/AppTopBar';
import React from 'react';
import { FC } from 'react';
import styled from 'styled-components';
import { Container } from './Container/Container';
import { T } from './Typography/Typography';

const StandardMainAreaElement = styled.main`
  grid-area: main;
  display: grid;
  grid-template-areas:
    'toparea rightsidebar'
    'page   rightsidebar'
    'bottombar rightsidebar';
  grid-template-columns: 1fr auto;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;
  /* Full height minus the 5px border throughout the app in My SaleCycle */
  height: calc(100vh - 5px);
`;

const SpinnerWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentAreaElement = styled.div<any>`
  background-color: ${({ theme, variation }) =>
    variation === 'primary' ? theme.vars.newColor.lightGS2 : 'transparent'};
  overflow: auto;
`;

const HeadingElement = styled.div`
  padding-top: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeadingButtonElement = styled.div``;

export type ContentAreaVariation = 'normal' | 'primary';

interface Props {
  variation?: ContentAreaVariation;
  saveBarEnabled?: boolean;
  padding?: string;
  heading?: string;
  subHeading?: string;
  headingButtonDisabled?: boolean;
  headingButtonText?: string;
  headingButtonAria?: string;
  isLoading?: boolean;
  isError?: boolean;
  errorHeading?: string;
  errorDescription?: string;
  agentComponent?: React.ReactNode;
  isAgentOpen?: boolean;
  onSaveButtonClicked?: () => void;
  onDiscardButtonClicked?: () => void;
  onHeadingButtonClicked?: () => void;
  saveButtonDisabled?: boolean;
}

export const StandardMainArea: FC<Props> = props => {
  const isPageLoadedWithoutErrors = () => !props.isError && !props.isLoading;
  return (
    <StandardMainAreaElement data-testid="StandardMainArea">
      <AppTopBar />
      <Page padding={0}>
        <Container padding={props.padding}>
          {props.isLoading && !props.isError && (
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
          )}
          {!props.isLoading && props.isError && (
            <Error heading={props.errorHeading} description={props.errorDescription} />
          )}
          {isPageLoadedWithoutErrors() && (
            <>
              <HeadingElement>
                <T variant="headingH1">{props.heading}</T>
                {props.headingButtonText && (
                  <HeadingButtonElement>
                    <Button
                      variation="primary"
                      onClick={props.onHeadingButtonClicked}
                      aria-label={props.headingButtonAria}
                      disabled={props.headingButtonDisabled}
                    >
                      {props.headingButtonText}
                    </Button>
                  </HeadingButtonElement>
                )}
              </HeadingElement>
              <P>{props.subHeading}</P>
              <Gap top={30} />
              <ContentAreaElement>{props.children}</ContentAreaElement>
            </>
          )}
        </Container>
      </Page>
      {props.saveBarEnabled && (
        <BottomBar>
          <SaveBar
            onSaveButtonClicked={props.onSaveButtonClicked}
            onDiscardButtonClicked={props.onDiscardButtonClicked}
            enableSaveButton={!props.saveButtonDisabled && isPageLoadedWithoutErrors()}
          />
        </BottomBar>
      )}
      {props.agentComponent && <RightSidebar isOpen={props.isAgentOpen ?? false}>{props.agentComponent}</RightSidebar>}
    </StandardMainAreaElement>
  );
};

const defaultProps: Props = {
  padding: '0px 50px 0px 50px',
};

StandardMainArea.defaultProps = defaultProps;
